import { useEffect, useState } from "react";
import { Loader2, Shield, Search, Download, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

export default function PaymentConsentsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("search", search.trim());
            const r = await api.get(`/admin/payment-consents?${params.toString()}`);
            setItems(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    const downloadCsv = async () => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        const url = `/admin/payment-consents/export.csv?${params.toString()}`;
        const r = await api.get(url, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `payment-consents-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <div data-testid="admin-payment-consents">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#1E3A8A]" /> Payment Consents
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Every parent who tapped <em>"I Agree — Show Payment Details"</em> on the Pay Now page. Immutable audit log for dispute protection.</p>
                </div>
                <button
                    onClick={downloadCsv}
                    disabled={items.length === 0}
                    data-testid="consents-export-csv"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-5">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, phone, email, student…"
                        data-testid="consents-search"
                        className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                    <Shield className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <div className="text-sm">No payment consents recorded yet.</div>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                                <th className="px-4 py-3 font-semibold">Accepted At</th>
                                <th className="px-4 py-3 font-semibold">Parent</th>
                                <th className="px-4 py-3 font-semibold">Phone</th>
                                <th className="px-4 py-3 font-semibold hidden md:table-cell">Email</th>
                                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Student</th>
                                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Terms</th>
                                <th className="px-4 py-3 font-semibold hidden xl:table-cell">IP</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((c) => (
                                <tr key={c.id} data-testid={`consent-row-${c.id}`} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                                        {new Date(c.accepted_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 font-medium text-[#0F172A]">
                                        <span className="inline-flex items-center gap-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                            {c.name}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs">{c.phone}</td>
                                    <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs">{c.email || "—"}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell text-slate-600 text-xs">{c.student_name || "—"}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell text-[10px] font-mono text-slate-400">{c.terms_version}</td>
                                    <td className="px-4 py-3 hidden xl:table-cell text-slate-400 font-mono text-xs">{c.ip || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
